<template>
  <div class="violation-date">
    {{ dateTime }}
    <v-btn
      v-if="$can('fines_edit_auto_driver')"
      class="ml-1"
      color="warning"
      icon
      small
      @click="opened = true"
    >
      <v-icon size="22">
        {{ dateTime ? 'mdi-pencil-circle-outline' : 'mdi-plus-circle-outline' }}
      </v-icon>
    </v-btn>

    <v-dialog
      v-model="opened"
      content-class="date-time-picker"
      max-width="350"
      persistent
    >
      <v-btn class="close-btn" icon @click="opened = false">
        <v-icon size="27" color="#eee"> mdi-close-circle-outline </v-icon>
      </v-btn>

      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-date-picker
            v-model="date"
            class="elevation-0"
            :locale="$i18n.locale"
            :max="new Date().toISOString().substr(0, 10)"
            full-width
          />
          <v-btn
            class="d-block ml-auto mr-3"
            color="warning"
            elevation="0"
            text
            :disabled="!date"
            @click="tab = 1"
          >
            {{ $t('common.button.next') }}
          </v-btn>
        </v-tab-item>

        <v-tab-item>
          <v-btn class="date-time-picker__back" icon @click="tab = 0">
            <v-icon size="27" color="#eee">mdi-arrow-left</v-icon>
          </v-btn>

          <v-time-picker
            v-model="time"
            format="24hr"
            class="elevation-0"
            full-width
          />

          <div class="date-time-picker__buttons">
            <v-btn
              class="d-block ml-auto mr-3"
              color="warning"
              elevation="0"
              text
              :disabled="!time"
              @click="tab = 2"
            >
              {{ $t('common.button.next') }}
            </v-btn>
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="violation-date__driver-label">
            <v-btn
              class="date-time-picker__back"
              icon
              :disabled="loading"
              @click="tab = 1"
            >
              <v-icon size="27" color="#eee">mdi-arrow-left</v-icon>
            </v-btn>

            {{ $t('notification.driver.label') }}
          </div>

          <div class="violation-date__driver-data">
            <div class="violation-date__driver-data-content">
              <div v-if="driverLoading" class="violation-date__driver-loader">
                <v-progress-circular color="primary" indeterminate />
              </div>

              <template v-else-if="driverData">
                <v-text-field
                  readonly
                  :value="driverData.user.name"
                  :label="$t('common.labels.name')"
                />
                <v-text-field
                  readonly
                  :value="driverData.user.phone"
                  :label="$t('common.fields.phone')"
                />
              </template>

              <div v-else class="violation-date__driver-no-data">
                <v-icon size="25" class="mb-1" color="#424242">
                  mdi-steering-off
                </v-icon>

                {{ $t('fines.noDriverFound') }}
              </div>
            </div>

            <v-btn
              class="d-block"
              color="warning"
              elevation="0"
              max-width="300px"
              block
              :loading="loading"
              @click="onSave"
            >
              {{ $t('common.btns.save') }}
            </v-btn>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    dateTime: {
      type: String,
      default: '',
    },
    carNumber: {
      type: String,
      default: '',
    },
    itemId: {
      type: Number,
      required: true,
    },
  },
  watch: {
    opened(value) {
      if (!value) {
        this.date = '';
        this.time = '';
        this.tab = 0;
      } else {
        if (!this.dateTime) return;

        const [date, time] = this.dateTime.split(' ');
        this.date = date;
        this.time = time;
      }
    },
    tab(val) {
      if (val === 2) this.fetchDriverByDate();
    },
  },
  data: () => ({
    opened: false,
    loading: false,
    driverLoading: false,
    driverData: null,
    tab: 0,
    date: '',
    time: '',
  }),
  computed: {
    resultValue() {
      const time = `${this.date} ${this.time}`;
      const withSeconds = time.length === 19;

      return withSeconds ? time : `${time}:00`;
    },
  },
  methods: {
    async onSave() {
      this.loading = true;

      const params = { violation_date: this.resultValue };

      if (this.driverData) params.driver_id = this.driverData.id;

      const result = await this.$store.dispatch('fines/updateItem', {
        id: this.itemId,
        params,
      });

      if (result.success) {
        this.opened = false;
      }

      this.$emit('saved', result);
      this.loading = false;
    },
    async fetchDriverByDate() {
      this.driverLoading = true;
      const { driver } = await this.$store.dispatch('fines/fetchDriverByDate', {
        number: this.carNumber,
        date: this.resultValue,
      });

      this.driverData = driver || null;
      this.driverLoading = false;
    },
  },
};
</script>

<style lang="scss">
.violation-date {
  display: flex;
  align-items: center;
  white-space: nowrap;

  &__driver-label {
    background-color: #424242;
    color: #fff;
    font-weight: 500;
    font-size: 20px;
    padding: 8px 0 7px 45px;
  }

  &__driver-data {
    position: relative;
    padding: 10px 10px 0;

    &-content {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      min-height: 136px;
    }
  }

  &__driver-no-data {
    font-weight: #424242;
    display: flex;
    flex-direction: column;
  }

  &__driver-loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.6);
    z-index: 1;
  }
}

.date-time-picker {
  background-color: #fff;
  padding-bottom: 10px;

  &__back {
    position: absolute;
    top: 5px;
    left: 5px;
    z-index: 10;
  }

  &__buttons {
    margin-top: 10px;
    padding-left: 15px;
    padding-right: 15px;
  }

  .v-time-picker-title {
    justify-content: center;
  }

  .v-btn--text {
    text-decoration: none !important;
  }
}
</style>
